import { IonBadge, IonIcon, IonItem, IonLabel, IonRange } from '@ionic/react';
import React, { ReactNode } from 'react';
import { copyTextToClipboard } from '../../common/util';
import { useToast } from '../../providers/ToastProvider';
import { Flip } from 'react-toastify';
import { TableWrapper } from './FIeldDetailRow.styles';
import { checkboxOutline, chevronForward, squareOutline } from 'ionicons/icons';

export interface IFieldDetailRow {
  value: string | number | boolean;
  title?: string;
  icon?: string;
  label?: string;
  labelIcon?: string;
  lines?: boolean;
  onClick?: (e: React.MouseEvent, val: string | number | boolean) => void;
  copyToClipboard?: boolean;
  isButton?: boolean;
  isRange?: boolean;
  placeholder?: string;
  valueNoWrap?: boolean;
  highlightColor?: string;
  slim?: boolean;
}

interface IFieldDetailRowGroup {
  rows: IFieldDetailRow[];
}

const FieldDetailRowGroup: React.FC<IFieldDetailRowGroup> = ({ rows }) => {
  const {
    actions: { infoToast },
  } = useToast();

  const hasIcon = rows.find((row) => row.icon || row.icon === '') ? true : false;
  const hasTitle = rows.find((row) => row.title);
  const hasLabel = rows.find((row) => row.label);
  const hasLabelIcon = rows.find((row) => row.labelIcon);
  const hasButton = rows.find((row) => row.isButton);

  const firstCol = hasIcon ? 'icon' : hasTitle ? 'title' : 'value';

  const handleClick = (e: React.MouseEvent, row: IFieldDetailRow) => {
    if (row.copyToClipboard && row.value) {
      copyTextToClipboard(row.value.toString());
      infoToast('Copied to clipboard', 1000, Flip);
    }
    row.onClick && row.onClick(e, row.value);
  };

  const Item: React.FC<{ children?: ReactNode; className?: string; lines?: boolean; slim?: boolean }> = ({ children, className, lines, slim }) => {
    return (
      <IonItem className={`${className || ''} field-item ion-no-padding ion-no-margin ${slim ? 'slim' : ''}`} lines={lines === undefined || lines ? 'full' : 'none'}>
        {children ? children : <></>}
      </IonItem>
    );
  };

  return (
    <TableWrapper>
      <tbody>
        {rows?.map((row, index) => (
          <tr key={index} onClick={(e) => handleClick(e, row)} className={`${row.isButton ? 'row-button' : ''} ${row.highlightColor ? 'row-highlight' : ''}`} style={row.highlightColor ? { border: `2px solid ${row.highlightColor}` } : {}}>
            {/* icon */}
            {hasIcon ? (
              row.icon || row.icon === '' ? (
                <td className={`${index < rows.length - 1 && row.lines !== false ? 'item-line' : ''} ${firstCol === 'icon' ? 'pad' : ''}`}>
                  <Item lines={false} className="icon" slim={row.slim}>
                    <IonLabel>
                      <IonIcon color="medium" icon={row.icon} size="small" slot="start" className="ion-no-margin" />
                    </IonLabel>
                  </Item>
                </td>
              ) : (
                <td className={`${index < rows.length - 1 && row.lines !== false ? 'item-line' : ''}`}>
                  <Item lines={false} slim={row.slim}></Item>
                </td>
              )
            ) : (
              <></>
            )}
            {/* title */}
            {hasTitle ? (
              row.title ? (
                <td className={`${index < rows.length - 1 && row.lines !== false ? 'item-line' : ''} ${firstCol === 'title' ? 'pad' : ''}`}>
                  <Item lines={false} className="height-100pc" slim={row.slim}>
                    <IonBadge color="light">
                      <IonLabel color="medium">{row.title}</IonLabel>
                    </IonBadge>
                  </Item>
                </td>
              ) : (
                <td className={`${index < rows.length - 1 && row.lines !== false ? 'item-line' : ''}`}>
                  <Item lines={false} slim={row.slim}></Item>
                </td>
              )
            ) : (
              <></>
            )}
            {/* value */}
            <td className={`width-100pc ${index < rows.length - 1 && row.lines !== false ? 'item-line' : ''} ${firstCol === 'value' ? 'pad' : ''}`}>
              {/* not boolean */}
              {typeof row.value !== 'boolean' && (
                <Item lines={false} slim={row.slim}>
                  {row.value && <IonLabel className={row.valueNoWrap ? '' : 'ion-text-wrap'}>{row.value}</IonLabel>}
                  {!row.value && (
                    <IonLabel className={row.valueNoWrap ? '' : 'ion-text-wrap'} color="medium">
                      {row.placeholder}
                    </IonLabel>
                  )}
                </Item>
              )}
              {/* boolean */}
              {typeof row.value === 'boolean' && (
                <Item lines={false} slim={row.slim}>
                  <IonLabel>{row.value === true ? <IonIcon icon={checkboxOutline} /> : <IonIcon icon={squareOutline} />}</IonLabel>
                </Item>
              )}
              {/* value number slider */}
              {typeof row.value === 'number' && row.isRange && (
                <Item lines={false} slim={row.slim}>
                  <IonRange className="ion-no-padding" ticks snaps value={row.value} />
                </Item>
              )}
            </td>
            {/* label or label icon */}
            {hasLabel || hasLabelIcon ? (
              <>
                {row.labelIcon ? (
                  <td className={`${index < rows.length - 1 && row.lines !== false ? 'item-line' : ''}`}>
                    <Item lines={false} slim={row.slim}>
                      <IonIcon color="medium" icon={row.labelIcon} size="small"></IonIcon>
                    </Item>
                  </td>
                ) : row.label ? (
                  <td className={`${index < rows.length - 1 && row.lines !== false ? 'item-line' : ''}`}>
                    <Item lines={false} slim={row.slim}>
                      <IonLabel color="medium">{row.label}</IonLabel>
                    </Item>
                  </td>
                ) : (
                  <td className={`${index < rows.length - 1 && row.lines !== false ? 'item-line' : ''}`}>
                    <Item lines={false} slim={row.slim}></Item>
                  </td>
                )}
              </>
            ) : (
              <></>
            )}

            {/* Button */}
            {hasButton ? (
              row.isButton ? (
                <td className={`${index < rows.length - 1 && row.lines !== false ? 'item-line' : ''}`}>
                  <IonItem lines="none" className="height-100pc">
                    <IonIcon icon={chevronForward} />
                  </IonItem>
                </td>
              ) : (
                <td className={`${index < rows.length - 1 && row.lines !== false ? 'item-line' : ''}`}>
                  <IonItem lines="none"></IonItem>
                </td>
              )
            ) : (
              <></>
            )}
          </tr>
        ))}
      </tbody>
    </TableWrapper>
  );
};

export default FieldDetailRowGroup;
